import { Property } from '../types';
import type { ProjectCardProject } from '../components/sections/ProjectCard';
import { catalogProgramGroupToLaunchCard } from './catalogProgramGroupToLaunchCard';
import type { CatalogProgramGroup } from './commercialisationLotsVitrine';

const DELIVERY_STATUSES = ['en-cours', 'planifie', 'livre', 'en-cours', 'planifie', 'livre'] as const;

export interface PropertyProgramGroup {
  key: string;
  lots: Property[];
  representative: Property;
}

export const getProgramGroupKey = (property: Property): string =>
  property.programKey?.trim() ||
  `${property.location.city}-${property.location.postalCode}`;

/** Résout une clé programme ou un id lot vers la clé `ville-codePostal`. */
export const resolveProgramKeyFromCatalog = (
  catalog: Property[],
  programKeyOrLotId: string
): string | null => {
  const groups = groupPropertiesByProgram(catalog);
  if (groups.some((group) => group.key === programKeyOrLotId)) {
    return programKeyOrLotId;
  }
  const byLot = groups.find((group) =>
    group.lots.some((lot) => lot.id === programKeyOrLotId)
  );
  return byLot?.key ?? null;
};

export const groupPropertiesByProgram = (
  properties: Property[]
): PropertyProgramGroup[] => {
  const groups: Record<string, PropertyProgramGroup> = {};

  for (const property of properties) {
    const key = getProgramGroupKey(property);
    if (!groups[key]) {
      groups[key] = { key, lots: [], representative: property };
    }
    groups[key].lots.push(property);
  }

  return Object.values(groups);
};

export const getProgramDisplayTitle = (group: PropertyProgramGroup): string => {
  if (group.lots.length <= 1) {
    return group.representative.title;
  }

  const residenceFromTitle = group.lots
    .map((lot: Property) => lot.title.match(/Résidence\s+[\wÀ-ÿ'-]+/i)?.[0])
    .find(Boolean);

  if (residenceFromTitle) {
    return residenceFromTitle;
  }

  return `Résidence ${group.representative.location.city}`;
};

export const buildProgramInfo = (group: PropertyProgramGroup) => ({
  name: getProgramDisplayTitle(group),
  location: `${group.representative.location.city} - ${group.representative.location.postalCode}`,
  image: group.representative.images[0] || '',
  deliveryDate: '4ème trim. 2026',
});

export const getResidenceName = (title: string): string => {
  const prefixPattern = /^(Appartement|Maison|Bureau|Local|Terrain)\s*(T\d+\s*)?-?\s*/i;
  const cleanTitle = title.replace(prefixPattern, '').trim();
  return cleanTitle || title;
};

export const getDefaultDeliveryDate = (property: Property): string => {
  if (property.availability) {
    return property.availability;
  }

  const currentYear = new Date().getFullYear();
  const quarters = ['1er trimestre.', '2ème trimestre.', '3ème trimestre.', '4ème trimestre.'];
  const statusIndex =
    property.status === 'disponible' ? 0 : property.status === 'reserve' ? 1 : 2;
  const quarterIndex = (parseInt(property.id, 10) + statusIndex) % 4;
  const year = currentYear + (parseInt(property.id, 10) % 3) + 1;

  return `${quarters[quarterIndex]} ${year}`;
};

const hashIndex = (id: string) => {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) {
    h = (h * 31 + id.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

export const propertyToProjectCard = (
  property: Property,
  index: number
): ProjectCardProject => {
  const statusIndex = hashIndex(property.id) % DELIVERY_STATUSES.length;
  const locationLabel = property.location.postalCode
    ? `${property.location.city.toUpperCase()} - ${property.location.postalCode}`
    : property.location.city.toUpperCase();

  return {
    id: property.programKey || property.id,
    title: getResidenceName(property.title),
    description: property.description,
    image: property.images[0] || '',
    type:
      property.type === 'appartement'
        ? 'collectif'
        : property.type === 'maison'
          ? 'maison'
          : property.type === 'bureau'
            ? 'bureaux'
            : property.type === 'local'
              ? 'activites'
              : 'terrain',
    status: DELIVERY_STATUSES[statusIndex] ?? 'en-cours',
    commune: property.location.city,
    surface: property.surfaceMinM2 ?? property.surface,
    surfaceMinM2: property.surfaceMinM2,
    surfaceMaxM2: property.surfaceMaxM2,
    logements: property.rooms,
    logementsDisplay: property.unitsCountDisplay,
    dateLivraison: property.availability || getDefaultDeliveryDate(property),
    tags: property.tags,
    avanceeTimeline: property.avanceeTimeline,
    priceMin: property.price > 0 ? property.price : undefined,
    priceMax: property.price > 0 ? property.price : undefined,
    location: locationLabel,
    streetAddress: property.location.address,
    conditionLabel: 'Neuf',
  };
};

export const programGroupToProjectCard = (
  group: PropertyProgramGroup,
  index: number
): ProjectCardProject => {
  const base = propertyToProjectCard(group.representative, index);
  const prices = group.lots
    .map((lot: Property) => lot.price)
    .filter((price: number) => price > 0);
  const priceMin = prices.length > 0 ? Math.min(...prices) : base.priceMin;
  const priceMax = prices.length > 0 ? Math.max(...prices) : base.priceMax;

  const lotSurfaces = group.lots.map((lot) => lot.surface).filter((s) => s > 0);
  const surfaceMinM2 =
    lotSurfaces.length > 0
      ? Math.min(...lotSurfaces)
      : group.representative.surfaceMinM2 ?? base.surfaceMinM2;
  const surfaceMaxM2 =
    lotSurfaces.length > 0
      ? Math.max(...lotSurfaces)
      : group.representative.surfaceMaxM2 ?? base.surfaceMaxM2;

  const useLotCountAsUnits = base.type === 'collectif' || base.type === 'maison';

  return {
    ...base,
    id: group.key,
    title: getResidenceName(getProgramDisplayTitle(group)),
    priceMin,
    priceMax,
    surface: surfaceMinM2 ?? base.surface,
    surfaceMinM2,
    surfaceMaxM2:
      surfaceMaxM2 != null && surfaceMinM2 != null && surfaceMaxM2 > surfaceMinM2
        ? surfaceMaxM2
        : undefined,
    logements:
      useLotCountAsUnits && group.lots.length > 1 ? group.lots.length : base.logements,
    logementsDisplay: base.logementsDisplay,
  };
};

/** @deprecated Préférer `catalogProgramGroupToLaunchCard` (import direct). */
export const catalogProgramGroupToProjectCard = catalogProgramGroupToLaunchCard;

/** Données complètes pour la page détail (sessionStorage). */
export const buildProjectDetailPayload = (
  property: Property,
  project: ProjectCardProject
) => ({
  ...project,
  images: property.images,
  contact: property.contact,
  features: property.features,
});
